import React from 'react';
import {
  Box,
  Button,
  Chip,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/joy';
import { MdGridView } from 'react-icons/md';
import { IconWrapper } from '../../../components/IconWrapper';
import type { VisualDesignerSurvey } from '../services/visualDesignerSurveys.service';

export type VisualDesignerPairingModel = {
  surveys: VisualDesignerSurvey[];
  selectedSurveyId: string | null;
  selectedSurveyLabel: string | null;
  itemCount: number;
  pairingLocked: boolean;
  attachable: boolean;
  onSelectSurvey: (id: string) => void;
  onOpenVisualDesigner: () => void;
};

export function VisualDesignerPairingCard({
  pairing,
  versionNumber,
  documentNoun,
  readOnly = false,
  hideHeading = false,
  surveyHints,
}: {
  pairing: VisualDesignerPairingModel;
  versionNumber?: number | null;
  documentNoun: 'quote' | 'estimate';
  readOnly?: boolean;
  hideHeading?: boolean;
  surveyHints?: Record<string, string>;
}) {
  const selected = pairing.surveys.find((survey) => survey.id === pairing.selectedSurveyId) || null;
  const label = selected?.label || pairing.selectedSurveyLabel;
  const hasSelection = !!label;
  const versionLabel = versionNumber != null ? `Version ${versionNumber}` : `this ${documentNoun}`;
  const kindLabel = documentNoun === 'quote' ? 'Quote' : 'Estimate';
  const pairedCaption = pairing.pairingLocked || pairing.attachable
    ? `Paired with ${kindLabel} ${versionLabel}`
    : selected?.documentVersionId
      ? 'Already paired with another version'
      : readOnly
        ? `Not paired with ${kindLabel} ${versionLabel}`
        : `Will pair with ${versionLabel} on save`;
  const itemCaption = pairing.itemCount > 0
    ? `Visual Designer • ${pairing.itemCount} item${pairing.itemCount === 1 ? '' : 's'}`
    : 'Visual Designer';

  return (
    <Box sx={{ mb: 2 }}>
      {!hideHeading && (
        <Typography level="title-sm" sx={{ mb: 1 }}>Visual Designer Link</Typography>
      )}
      <Box
        sx={{
          p: 1.5,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 'md',
          bgcolor: 'background.surface',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          gap={1.5}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
        >
          <Stack direction="row" gap={1.25} alignItems="flex-start" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 'sm',
                border: '1px solid',
                borderColor: 'neutral.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'neutral.600',
                flexShrink: 0,
              }}
            >
              <IconWrapper icon={MdGridView} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                <Typography level="title-sm">
                  {hasSelection ? label : 'No visual design paired'}
                </Typography>
                {readOnly && pairing.pairingLocked && (
                  <Chip size="sm" variant="soft" color="neutral">Locked</Chip>
                )}
              </Stack>
              <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                {hasSelection
                  ? itemCaption
                  : readOnly
                    ? 'No survey is paired with this version.'
                    : 'Choose a survey to attach to this version.'}
              </Typography>
              {hasSelection && (
                <Typography level="body-xs" fontWeight="lg" sx={{ mt: 0.35 }}>
                  {pairedCaption}
                </Typography>
              )}
            </Box>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={1} sx={{ flexShrink: 0 }}>
            {!readOnly && (
              <Dropdown>
                <MenuButton
                  size="sm"
                  variant="solid"
                  color="primary"
                  disabled={pairing.pairingLocked || pairing.surveys.length === 0}
                >
                  Change Selection
                </MenuButton>
                <Menu placement="bottom-end" sx={{ minWidth: 220 }}>
                  {pairing.surveys.map((survey) => (
                    <MenuItem
                      key={survey.id}
                      selected={survey.id === pairing.selectedSurveyId}
                      onClick={() => pairing.onSelectSurvey(survey.id)}
                    >
                      {survey.label}
                      {survey.id === pairing.selectedSurveyId
                        ? ' · this version'
                        : surveyHints?.[survey.id]
                          ? ` · ${surveyHints[survey.id]}`
                          : survey.documentVersionId
                            ? ' · another version'
                            : ' · unpaired'}
                    </MenuItem>
                  ))}
                </Menu>
              </Dropdown>
            )}
            <Button size="sm" variant="outlined" onClick={pairing.onOpenVisualDesigner}>
              Open Visual Designer
            </Button>
          </Stack>
        </Stack>
        {pairing.pairingLocked && !readOnly && (
          <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1 }}>
            This transmitted version keeps its survey. Unlock override for a small change, or switch to an unlocked version.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
